import { CheckCircle, CloseRounded } from "@mui/icons-material";
import {
    Avatar,
    Box,
    CircularProgress,
    Icon,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    ATTACHMENT_LEVEL,
    ATTACHMENT_STATUS,
} from "constants/generalConstants";
import useAttachments from "hooks/useAttachments";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

const FileItem = ({ item }) => {
    const isPostPoReq = useSelector(
        (state) => state.createRequisition.isPostPoReq
    );
    const isAmendedReq = useSelector(
        (state) => state.createRequisition.amendedReq
    );
    const dispatch = useDispatch();
    const { deleteAttachment } = useAttachments();

    const deleteFileAttachment = async (attachment) => {
        await deleteAttachment(attachment, dispatch);
    };

    const formatBytes = (bytes) => {
        const kb = bytes / 1024;
        if (kb <= 100) {
            return `${kb.toFixed(2)} KB`;
        } else {
            const mb = kb / 1024;
            return `${mb.toFixed(2)} MB`;
        }
    };

    const isStatusComplete =
        !item.status || item.status === ATTACHMENT_STATUS.SCAN_COMPLETED;

    let tooltipTextForDeletion = `Delete ${item.TITLE}`;
    if (!item.isNewUpload) {
        if (isPostPoReq) {
            tooltipTextForDeletion = `Files cannot be deleted after a Purchase Order has been created.`;
        } else if (isAmendedReq) {
            tooltipTextForDeletion = `${item.TITLE} can be deleted in the next screen if necessary.`;
        }
    }

    const getStatusMessage = () => {
        switch (item.status) {
            case ATTACHMENT_STATUS.UPLOADING:
                return "File upload in progress......";
            case ATTACHMENT_STATUS.COPYING:
                return "File copy in progress......";
            case ATTACHMENT_STATUS.SCAN_PENDING:
                return "Malware scan in progress......";
            default:
                return "";
        }
    };

    return (
        <Stack
            direction="row"
            spacing={2}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
        >
            <Stack direction="row" spacing={2}>
                <Avatar
                    sx={{
                        bgcolor:
                            !item.isNewUpload && isAmendedReq
                                ? "grey.400"
                                : "grey.100",
                    }}
                >
                    {isStatusComplete ? (
                        <Icon color="success">
                            <CheckCircle />
                        </Icon>
                    ) : (
                        <CircularProgress size={24} />
                    )}
                </Avatar>
                <Stack direction="column">
                    {(item.status === ATTACHMENT_STATUS.COPYING ||
                        item.status === ATTACHMENT_STATUS.UPLOADING ||
                        item.status === ATTACHMENT_STATUS.SCAN_PENDING) && (
                        <Typography
                            variant="body2"
                            style={{
                                whiteSpace: "normal",
                                overflowWrap: "break-word",
                                wordBreak: "break-all",
                                color: "#FFBF00",
                            }}
                        >
                            {getStatusMessage()}
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        style={{
                            whiteSpace: "normal",
                            overflowWrap: "break-word",
                            wordBreak: "break-all",
                        }}
                    >
                        {item.FILENAME}
                    </Typography>
                    {item?.FILE_SIZE && (
                        <Typography variant="body2">
                            {formatBytes(item?.FILE_SIZE)}
                        </Typography>
                    )}
                </Stack>
            </Stack>
            {isStatusComplete && (
                <Box sx={{ paddingRight: 1 }}>
                    <Tooltip title={tooltipTextForDeletion}>
                        <span>
                            <IconButton
                                edge="end"
                                aria-label="delete"
                                disabled={!item.isNewUpload && isAmendedReq}
                                onClick={() => {
                                    deleteFileAttachment(item);
                                }}
                            >
                                <CloseRounded />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}
        </Stack>
    );
};

export default function UploadedFiles({ titleText, mode }) {
    let filteredAttachments = [];
    const { headerAttachments, getAttachmentsByUniqueLineId, attachments } =
        useAttachments();

    const levelType = useSelector((state) => state.attachment.levelType);
    const currentLineUniqueLineId = useSelector(
        (state) => state.attachment.attachmentLineItem.uniqueLineId
    );

    if (mode === "Upload") {
        if (levelType === ATTACHMENT_LEVEL.HEADER) {
            filteredAttachments = headerAttachments;
        } else {
            filteredAttachments = getAttachmentsByUniqueLineId(
                currentLineUniqueLineId
            );
        }
    } else {
        filteredAttachments = attachments;
    }

    if (!filteredAttachments || filteredAttachments.length === 0) {
        return (
            <Paper
                sx={{
                    p: 3,
                    minWidth: 300,
                    borderRadius: 2,
                    border: "2px dashed",
                    borderColor: "grey.300",
                    boxShadow: 0,
                    color: "grey.500",
                }}
            >
                No files selected yet ...
            </Paper>
        );
    }

    return (
        <Paper
            sx={{
                maxWidth: "100%",
                minWidth: 250,
                borderRadius: 2,
                border: "2px dashed",
                borderColor: "grey.300",
                boxShadow: 0,
                color: "grey.500",
            }}
        >
            <Stack gap={1}>
                <Typography
                    variant="h6"
                    component="h6"
                    sx={{ px: 2, fontSize: 14, fontWeight: 600 }}
                >
                    {titleText}
                </Typography>

                <Stack
                    gap={2}
                    sx={{
                        p: 1,
                        maxWidth: "100%",
                        bgcolor: "background.paper",
                    }}
                >
                    {filteredAttachments?.map((item, index) => {
                        return <FileItem key={index} item={item} />;
                    })}
                </Stack>
            </Stack>
        </Paper>
    );
}
